import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Check, CheckCircle2 } from "lucide-react";
import { PropostaAceite } from "@/components/PropostaAceiteDialog";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { netCommission } from "@/components/PartilhaComissao";
import { fmtCurrency, isFeminine } from "@/lib/format";
import { SignDateDialog, SIGNED_KEY } from "@/components/SignDateDialog";
import { ExclusiveSetter } from "@/components/ExclusiveSetter";
import { openMsgs, twilioFor, sendWa } from "@/lib/whatsapp";
import { openContacts } from "@/components/ProcessSidebar";

export const navyBtn = "w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-[15px] font-semibold navy-bg text-white hover:brightness-110 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed";
export const glow = "ring-4 ring-green-500/20 shadow-[0_0_0_1px_rgba(34,197,94,0.45),0_6px_24px_rgba(34,197,94,0.35)]";

const ownersWord = (p) => {
  const s = p.sellers || [];
  return s.length > 1 ? "Proprietários" : s[0] && isFeminine(s[0]) ? "Proprietária" : "Proprietário";
};

function cmiToast(p, onReview) {
  const c = netCommission(p);
  toast.custom((t) => (
    <div className="flex items-center gap-4 rounded-xl navy-bg text-white px-5 py-4 shadow-2xl w-[380px] max-w-[92vw]" data-testid="cmi-generated-toast">
      <CheckCircle2 size={26} className="text-green-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[15px]"><b>CMI gerado.</b> Agora é só assinar.</div>
        {c != null && <div className="text-[13px] text-white/75" data-testid="cmi-generated-carrot">Mais um passo para os <span className="whitespace-nowrap">{fmtCurrency(c)}</span> de comissão</div>}
      </div>
      <button onClick={() => { toast.dismiss(t); onReview(); }} className="font-semibold underline underline-offset-2 shrink-0" data-testid="cmi-generated-review">Rever CMI</button>
    </div>
  ), { duration: 8000 });
}

function SignDateStep({ p, sub, onDone }) {
  const [ai, setAi] = useState(null);
  const doc = (p.documents || []).find((d) => d.key === SIGNED_KEY);
  return (
    <div data-testid="next-step">
      <button onClick={() => setAi(doc?.ai || {})} className={navyBtn} data-testid="next-step-btn">Indicar data de assinatura</button>
      <div className="text-[13px] text-slate-500 text-center mt-1.5">{sub}</div>
      {ai && <SignDateDialog pid={p.id} ai={ai} onClose={() => setAi(null)} onSaved={onDone} />}
    </div>
  );
}

export const PLACES = ["Cartório", "Casa Pronta", "Advogado / solicitador", "Banco"];

export function DeedFields({ time, setTime, place, setPlace }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <label><span className="text-[12px] text-slate-500">Hora</span>
        <select value={time} onChange={(e) => setTime(e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="deed-time">
          <option value="">—</option>
          {Array.from({ length: 48 }, (_, i) => `${String(8 + Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`).map((t) => <option key={t} value={t}>{t}</option>)}
        </select></label>
      <label><span className="text-[12px] text-slate-500">Local</span>
        <input list="deed-places" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Ex.: Cartório Notarial de Lisboa" className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="deed-place" />
        <datalist id="deed-places">{PLACES.map((x) => <option key={x} value={x} />)}</datalist></label>
    </div>
  );
}

function DeedDialog({ onSave }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  return (
    <>
      <button onClick={() => setOpen(true)} className={navyBtn} data-testid="next-step-btn">Marcar escritura</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm space-y-1" data-testid="deed-dialog">
          <DialogHeader><DialogTitle className="font-heading navy-text">Marcar escritura</DialogTitle></DialogHeader>
          <span className="text-[12px] text-slate-500">Data</span>
          <DateInput autoFocus data-testid="next-deed-date" onChange={(e) => setDate(e.target.value)} />
          <DeedFields time={time} setTime={setTime} place={place} setPlace={setPlace} />
          <button disabled={!date} onClick={() => { onSave(date, { deed_time: time || null, deed_place: place.trim() || null }); setOpen(false); }} className={`${navyBtn} mt-2`} data-testid="next-deed-save">Guardar</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function NextStep({ p, onDone, onPanel, onFocus, onUpload, onSaveDeed, preload, onPreloadUsed }) {
  const [busy, setBusy] = useState(false);
  const ns = p.next_step;
  if (!ns) return null;
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/processes/${p.id}/cmi/generate`);
      onDone();
      if (data.missing) { toast.success(`CMI v${data.n} gerado · ${data.missing} campos em branco`); onPanel("cmi"); }
      else cmiToast(p, () => onPanel("cmi"));
    }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendBuyerLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/processes/${p.id}/portal-links`);
      const lk = (data.links || []).find((l) => l.role === "comprador" && (l.party_index || 0) === 0);
      if (!lk?.phone) { openContacts("[data-testid=party-comprador]"); return; }
      const { data: s } = await api.post(`/processes/${p.id}/portal-links/${lk.token}/send`, { channel: "whatsapp", base_url: window.location.origin });
      await sendWa(p.id, lk.phone, s.wa_text);
      onDone();
    }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (ns.key === "proposta") {
    return (
      <div data-testid="next-step">
        <PropostaAceite pid={p.id} p={p} askingPrice={p.mediation?.asking_price} onDone={onDone} preload={preload} onPreloadUsed={onPreloadUsed} label="Registar proposta aceite" className={navyBtn} />
        {ns.sub && <div className="text-xs text-slate-500 text-center mt-1.5" data-testid="next-step-sub">{ns.sub}</div>}
      </div>
    );
  }
  if (ns.key === "aguardar_proposta") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="next-step">
        <div className="text-[15px] font-semibold navy-text" data-testid="next-step-label">{ns.label}</div>
        {ns.sub && <div className="text-xs text-slate-500 mt-0.5" data-testid="next-step-sub">{ns.sub}</div>}
      </div>
    );
  }
  if (ns.key === "marcar_escritura") return <div data-testid="next-step"><DeedDialog onSave={onSaveDeed} /></div>;
  if (ns.key === "data_cmi") return <SignDateStep p={p} sub={ns.sub} onDone={onDone} />;
  if (ns.key === "wa_classificar") {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="next-step">
        <div className="flex-1 min-w-0 text-[15px] font-semibold text-amber-900" data-testid="next-step-sub">{ns.sub}</div>
        <button onClick={() => openMsgs({ loose: true })} className="h-10 px-5 rounded-[12px] bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors" data-testid="next-step-btn">Ver</button>
      </div>
    );
  }
  const goTo = (sel, click) => {
    const el = document.querySelector(sel);
    if (!el) return false;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (click) el.click();
    return true;
  };
  const act = { add_titular: () => onPanel("add-titular"), gerar_cmi: generate, rever_cmi: () => onPanel("cmi"), assinar_cmi: () => onPanel("cmi-sign"), carregar_cmi: () => onPanel("cmi-sign"), carregar_cpcv: onUpload,
    pedir_certidao: () => onFocus("certidao_permanente"),
    pedir_docs: () => goTo("[data-remind=vendedor]", true) || onPanel("cmi"), tratar_docs: () => goTo("[data-testid=treat-zone]"),
    link_comprador: () => (twilioFor(p.id) ? sendBuyerLink() : openContacts("[data-testid=party-comprador-wa]", true)) }[ns.key];
  const miss = ns.missing || [];
  const ready = ns.key === "gerar_cmi" && ns.enabled !== false;
  return (
    <div data-testid="next-step">
      {ns.ask_exclusive && <div className="mb-2"><ExclusiveSetter p={p} onDone={onDone} /></div>}
      <div className="flex gap-2">
        <button onClick={act} disabled={busy || ns.enabled === false} className={`${navyBtn} ${ready ? glow : ""}`} data-testid="next-step-btn" data-ready={ready ? "1" : "0"}>
          {ready && <Check size={17} strokeWidth={2.5} />}{ns.label}
        </button>
      </div>
      {ready && <div className="text-[13px] text-slate-500 text-center mt-1.5" data-testid="next-step-sub">{ownersWord(p)}, imóvel e condições preenchidos</div>}
      {!ready && ns.sub && ns.key !== "gerar_cmi" && <div className="text-xs text-slate-500 text-center mt-1.5" data-testid="next-step-sub">{ns.sub}</div>}
      {ns.key === "gerar_cmi" && miss.length > 0 && (
        <div className="text-xs text-slate-500 mt-1.5" data-testid="next-step-missing">
          {miss.length === 1 ? "Falta 1 dado" : `Faltam ${miss.length} dados`}: {miss.join(", ")} · <button onClick={generate} disabled={busy} className="underline font-semibold navy-text" data-testid="next-step-force">Gerar mesmo assim</button>
        </div>
      )}
    </div>
  );
}
